'use client'

import React, { useRef, useLayoutEffect, useState, useCallback } from 'react'
import { cn } from '@/utils/ui'

type ScrollToActiveContainerProps = {
  children: React.ReactNode
  className?: string
  gradientFromClass?: string
}

export const ScrollToActiveContainer: React.FC<ScrollToActiveContainerProps> = ({
  children,
  className,
  gradientFromClass = 'from-blue-98',
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Scroll to first active element
    const firstActive = container.querySelector('[data-active="true"]')
    if (firstActive) {
      const containerRect = container.getBoundingClientRect()
      const activeRect = firstActive.getBoundingClientRect()
      const scrollLeft =
        activeRect.left - containerRect.left + container.scrollLeft - containerRect.width / 2 + activeRect.width / 2
      container.scrollLeft = Math.max(0, scrollLeft)
    }

    updateScrollState()

    container.addEventListener('scroll', updateScrollState, { passive: true })
    const ro = new ResizeObserver(updateScrollState)
    ro.observe(container)

    return () => {
      container.removeEventListener('scroll', updateScrollState)
      ro.disconnect()
    }
  }, [updateScrollState])

  return (
    <div className="relative">
      <div ref={containerRef} className={cn(className, 'scrollbar-hide')}>
        {children}
      </div>
      {/* Left gradient */}
      <div
        className={cn(
          `pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r ${gradientFromClass} to-transparent transition-opacity duration-150`,
          canScrollLeft ? 'opacity-100' : 'opacity-0',
        )}
      />
      {/* Right gradient */}
      <div
        className={cn(
          `pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l ${gradientFromClass} to-transparent transition-opacity duration-150`,
          canScrollRight ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  )
}
